import {
  Box,
  Button,
  Flex,
  Heading,
  Table,
  Tbody,
  Th,
  Thead,
  Tr,
} from '@ritualco/jutsu'
import { AddIcon } from '@chakra-ui/icons'
import { FC, ReactNode } from 'react'

import { Pagination, PaginationProps } from 'components/Pagination'
import { RestaurantList } from 'types/entities'
import { useAnalytics } from 'lib/simple-analytics'
import { useContent } from 'lib/content/react'
import { renderTbody, useMyListsTable } from './util'
import { AnalyticEvents } from './types'

type TableActionBarProps = {
  heading: ReactNode
}
const TableActionBar: FC<TableActionBarProps> = ({ heading, children }) => (
  <Flex alignItems="center" justifyContent="space-between" py={3}>
    <Heading size="lg" fontWeight="medium" px={2} flex="1">
      {heading}
    </Heading>

    <Box>{children}</Box>
  </Flex>
)

type MyListTableProps = {
  restaurantLists: RestaurantList[]
  onCreateNewList: () => void
} & Pick<
  PaginationProps,
  | `${'on' | 'has'}${'Next' | 'Prev'}`
  | 'onRowsPerPageChange'
  | 'rowsPerPageOptions'
  | 'rowsPerPage'
>
export const MyListsTable: FC<MyListTableProps> = ({
  restaurantLists,
  onCreateNewList,
  ...paginationProps
}) => {
  const table = useMyListsTable({ restaurantLists })
  const { track } = useAnalytics()
  const { formatMessage } = useContent()

  const onNewListButtonClick = () => {
    track(AnalyticEvents.createNewListClicked)
    onCreateNewList()
  }

  return (
    <Box>
      <TableActionBar
        heading={formatMessage({
          id: 'pmportal.restaurantLists.listsTableTitle',
        })}
      >
        <Button onClick={onNewListButtonClick} leftIcon={<AddIcon />}>
          {formatMessage({
            id: 'pmportal.restaurantLists.listsCreate',
          })}
        </Button>
      </TableActionBar>
      <Table {...table.getTableProps()}>
        <Thead>
          {table.headerGroups.map(headerGroup => (
            <Tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map(column => (
                <Th px={2} py={3} {...column.getHeaderProps()}>
                  {column.render('Header')}
                </Th>
              ))}
            </Tr>
          ))}
        </Thead>
        <Tbody {...table.getTableProps()}>
          {table.rows.map(renderTbody(table.prepareRow))}
        </Tbody>
      </Table>
      <Pagination fullWidth rowsPerPage={10} {...paginationProps} />
    </Box>
  )
}
